import { IImageListItem } from '@/components/GoodsDetail/handleData';
// CSS已合并到 globals.css，无需单独导入
import Image from '@/components/Image';
import { Box } from '@mui/material';
import { LeftArrowOutlined } from '@zeelool/icon';
import { useTranslations } from 'next-intl';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Swiper as SwiperType } from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

interface ProductGalleryProps {
  images: IImageListItem[];
  isGlass?: boolean;
  productName?: string;
}

const ProductGallery = forwardRef<{ swiper: SwiperType }, ProductGalleryProps>(
  ({ images, isGlass, productName }, ref) => {
    const t = useTranslations('account.redeemableItems.RedeemedDetailDialog.productGallery');
    const swiperRef = useRef<SwiperType | null>(null);
    const [paginationShow, setPaginationShow] = useState<boolean>(true);
    useImperativeHandle(ref, () => ({
      swiper: swiperRef.current as SwiperType,
    }));

    const swiperOptions = {
      modules: [Navigation, Pagination],
      navigation: {
        nextEl: '.swiper-button-next-custom',
        prevEl: '.swiper-button-prev-custom',
      },
      pagination: {
        el: '.swiper-pagination-custom',
        type: 'fraction' as const,
        formatFractionCurrent: (number) => number,
        formatFractionTotal: (number) => number,
        renderFraction(currentClass, totalClass) {
          return `<div class="w-fit px-[14px] py-[3px] bg-[#00000040] rounded-[10px] text-white text-[12px] leading-[14px]">
          <span class="${currentClass}"></span>
          <span>/</span>
          <span class="${totalClass}"></span>
        </div>`;
        },
      },
      slidesPerView: 1,
      spaceBetween: 20,
      className: 'product-swiper',
      grabCursor: true,
    };

    const videoRef = useRef<HTMLVideoElement>(null);

    const handleSlideChange = () => {
      if (!swiperRef.current) return;
      const { activeIndex } = swiperRef.current;
      const activeItem = images[activeIndex];
      if (videoRef.current) {
        if (activeItem.goodsType === 'video') {
          videoRef.current.play();
          setPaginationShow(false);
        } else {
          videoRef.current.pause();
          setPaginationShow(true);
        }
      }
    };

    const handleSwiper = (swiper: SwiperType) => {
      swiperRef.current = swiper;
    };

    return (
      <Box className="top-0 left-0 absolute bg-auxiliary-light-gray w-[400px] h-[400px] group">
        <Box className="relative">
          <Swiper {...swiperOptions} onSlideChange={handleSlideChange} onSwiper={handleSwiper}>
            {images.map((imageItem, index) => (
              <SwiperSlide key={imageItem.url}>
                <Box className="w-[400px] h-[400px] flex items-center justify-center bg-auxiliary-light-gray">
                  {imageItem.goodsType === 'video' && (
                    <video
                      ref={videoRef}
                      className="w-full h-full"
                      controls
                      playsInline
                      controlsList="nodownload noplaybackrate noremoteplayback"
                      disablePictureInPicture
                      loop
                      preload="auto"
                      onClick={(e) => e.stopPropagation()}
                      muted
                    >
                      <source src={imageItem.url} />
                    </video>
                  )}
                  {imageItem.goodsType !== 'video' && (
                    <Image
                      src={imageItem.url}
                      alt={`${productName || t('defaultProductName')}-glasses-${index}`}
                      width={isGlass && imageItem.goodsType === 'product' ? 279 : 400}
                      height={isGlass && imageItem.goodsType === 'product' ? 279 : 400}
                      className={`object-cover ${isGlass && imageItem.goodsType === 'product' ? 'mix-blend-multiply' : ''}`}
                    />
                  )}
                </Box>
              </SwiperSlide>
            ))}
          </Swiper>
          {/* 自定义导航按钮 */}
          <div className="swiper-button-prev-custom absolute top-1/2 -translate-y-1/2 left-[12px] z-10 w-[34px] h-[34px] bg-[#000000]/60 rounded-full flex items-center justify-center cursor-pointer transition-all duration-300 hover:bg-[#000000]/70 hover:scale-110 opacity-0 group-hover:opacity-100 [&.swiper-button-disabled]:hidden">
            <LeftArrowOutlined className="text-[#ffffff] text-[16px]" />
          </div>
          <div className="swiper-button-next-custom absolute top-1/2 -translate-y-1/2 right-[12px] z-10 w-[34px] h-[34px] bg-[#000000]/60 rounded-full flex items-center justify-center cursor-pointer transition-all duration-300 hover:bg-[#000000]/70 hover:scale-110 opacity-0 group-hover:opacity-100 [&.swiper-button-disabled]:hidden">
            <LeftArrowOutlined className="text-[#ffffff] text-[16px] rotate-180" />
          </div>

          {/* 自定义分页器 */}
          <div
            className={`swiper-pagination-custom absolute !bottom-16 !left-16 z-10 ${
              paginationShow ? 'opacity-100' : 'opacity-0'
            }`}
          />
        </Box>
      </Box>
    );
  },
);

ProductGallery.displayName = 'ProductGallery';

export default ProductGallery;
